(() => {
  'use strict';

  const DEFAULT_DRAWER_SECTION = 'wub-cart-drawer';
  const SOURCE = 'wub-commerce';
  const QUANTITY_DEBOUNCE_MS = 180;

  let lastTrigger = null;
  let pendingExternalAdd = false;
  let pendingExternalTrigger = null;
  let externalRefreshTimer = null;
  let drawerRefreshPromise = null;
  let drawerMutationChain = Promise.resolve();
  let toastTimer = null;

  const quantityTimers = new Map();
  const pendingLineQuantities = new Map();
  const drawerMutationErrors = new Map();

  const routes = () => window.WUB?.routes || {};
  const rootUrl = () => window.Shopify?.routes?.root || routes().root_url || '/';
  const cartUrl = () => routes().cart_url || `${rootUrl()}cart`;
  const cartAddUrl = () => routes().cart_add_url || `${rootUrl()}cart/add`;
  const cartChangeUrl = () => routes().cart_change_url || `${rootUrl()}cart/change`;
  const i18n = (group, key, fallback = '') => window.WUB?.i18n?.[group]?.[key] || fallback;

  function jsonEndpoint(url) {
    return url.endsWith('.js') ? url : `${url}.js`;
  }

  function escapeCss(value) {
    return window.CSS?.escape
      ? window.CSS.escape(String(value))
      : String(value).replace(/["\\]/g, '\\$&');
  }

  function drawerSectionRoot() {
    return document.querySelector('[data-wub-cart-drawer-section]');
  }

  function drawerSectionId() {
    return drawerSectionRoot()?.dataset?.wubCartSectionId || DEFAULT_DRAWER_SECTION;
  }

  function drawer() {
    return drawerSectionRoot()?.querySelector('#WubCartDrawer') || document.getElementById('WubCartDrawer');
  }

  function panel() {
    return drawer()?.querySelector('.wub-cart-drawer__panel') || null;
  }

  function drawerBody() {
    return drawer()?.querySelector('[data-wub-cart-drawer-body]') || null;
  }

  function cartCountFromDrawer() {
    const value = parseInt(drawerSectionRoot()?.dataset?.wubCartItemCount || '0', 10);
    return Number.isFinite(value) ? value : 0;
  }

  function currentHeaderCount() {
    const badge = document.querySelector('[data-wub-header] [data-wub-cart-count]');
    const value = parseInt(badge?.textContent || '0', 10);
    return Number.isFinite(value) ? value : 0;
  }

  function updateHeaderCount(count) {
    if (!Number.isFinite(count)) return;
    document.querySelectorAll('[data-wub-header] [data-wub-cart-count]').forEach((badge) => {
      badge.textContent = String(count);
      badge.hidden = count < 1;
    });
  }

  function emitCartUpdated(count, extra = {}) {
    document.dispatchEvent(new CustomEvent('cart:updated', {
      detail: { source: SOURCE, item_count: count, ...extra }
    }));
  }

  function emit(name, detail = {}) {
    document.dispatchEvent(new CustomEvent(name, { detail }));
  }

  function parseDrawerRoot(html) {
    if (!html) return null;
    const doc = new DOMParser().parseFromString(html, 'text/html');
    return doc.querySelector('[data-wub-cart-drawer-section]');
  }

  function interactionToken(element) {
    if (!element) return null;
    if (element.matches('[data-wub-drawer-quantity-input]')) return 'quantity-input';
    if (element.matches('[data-wub-drawer-remove]')) return 'remove';
    if (element.matches('[data-wub-checkout]')) return 'checkout';
    if (element.matches('[data-wub-cart-close]')) return 'close';
    if (element.matches('.wub-cart-drawer__continue-link')) return 'continue';
    const quantityAction = element.closest('[data-wub-drawer-quantity-action]');
    if (quantityAction) return `quantity-${quantityAction.dataset.wubDrawerQuantityAction || ''}`;
    return null;
  }

  function captureDrawerInteraction() {
    const currentDrawer = drawer();
    const body = drawerBody();
    const active = document.activeElement;
    const inside = currentDrawer && active instanceof Element && currentDrawer.contains(active);
    const line = inside ? active.closest('[data-wub-cart-line-key]') : null;

    return {
      wasOpen: currentDrawer?.classList.contains('is-open') === true,
      scrollTop: body?.scrollTop || 0,
      lineKey: line?.dataset?.wubCartLineKey || null,
      token: inside ? interactionToken(active) : null
    };
  }

  function findInteractionTarget(state) {
    if (!state?.token) return null;
    const currentDrawer = drawer();
    if (!currentDrawer) return null;

    const scope = state.lineKey
      ? currentDrawer.querySelector(`[data-wub-cart-line-key="${escapeCss(state.lineKey)}"]`)
      : currentDrawer;
    if (!scope) return null;

    if (state.token === 'quantity-input') return scope.querySelector('[data-wub-drawer-quantity-input]');
    if (state.token === 'remove') return scope.querySelector('[data-wub-drawer-remove]');
    if (state.token === 'checkout') return currentDrawer.querySelector('[data-wub-checkout]');
    if (state.token === 'close') return currentDrawer.querySelector('.wub-cart-drawer__close');
    if (state.token === 'continue') return currentDrawer.querySelector('.wub-cart-drawer__continue-link');
    if (state.token.startsWith('quantity-')) {
      const action = state.token.replace('quantity-', '');
      return scope.querySelector(`[data-wub-drawer-quantity-action="${escapeCss(action)}"]`);
    }
    return null;
  }

  function applyPendingLineQuantities() {
    pendingLineQuantities.forEach((quantity, key) => {
      const line = drawer()?.querySelector(`[data-wub-cart-line-key="${escapeCss(key)}"]`);
      const input = line?.querySelector('[data-wub-drawer-quantity-input]');
      if (input) input.value = String(quantity);
    });
  }

  function restoreDrawerInteraction(state) {
    if (!state?.wasOpen) return;
    const currentDrawer = drawer();
    const currentPanel = panel();
    const body = drawerBody();
    if (!currentDrawer || !currentPanel) return;

    currentDrawer.classList.add('is-open');
    currentDrawer.setAttribute('aria-hidden', 'false');
    document.body.classList.add('wub-commerce-locked');
    applyPendingLineQuantities();

    window.requestAnimationFrame(() => {
      if (body) body.scrollTop = state.scrollTop;
      const target = findInteractionTarget(state);
      if (target && !target.disabled) target.focus({ preventScroll: true });
      else currentPanel.focus({ preventScroll: true });
      if (body) body.scrollTop = state.scrollTop;
    });
  }

  function applyDrawerRoot(next) {
    const current = drawerSectionRoot();
    if (!next || !current) return false;

    const interaction = captureDrawerInteraction();
    current.replaceWith(next);
    updateHeaderCount(cartCountFromDrawer());
    restoreDrawerInteraction(interaction);
    return true;
  }

  function applyDrawerHtml(html) {
    return applyDrawerRoot(parseDrawerRoot(html));
  }

  function syncQuantityControl(currentLine, nextLine, key) {
    const currentInput = currentLine.querySelector('[data-wub-drawer-quantity-input]');
    const nextInput = nextLine.querySelector('[data-wub-drawer-quantity-input]');
    if (!currentInput || !nextInput) return false;

    ['min', 'step', 'max'].forEach((attribute) => {
      if (nextInput.hasAttribute(attribute)) currentInput.setAttribute(attribute, nextInput.getAttribute(attribute));
      else currentInput.removeAttribute(attribute);
    });

    const serverQuantity = parseInt(nextInput.value || nextInput.getAttribute('value') || '1', 10) || 1;
    const pendingQuantity = pendingLineQuantities.get(key);
    const displayQuantity = Number.isFinite(pendingQuantity) ? pendingQuantity : serverQuantity;
    currentInput.value = String(displayQuantity);
    currentInput.setAttribute('value', String(displayQuantity));

    const min = Math.max(1, parseInt(currentInput.min || '1', 10) || 1);
    const maxRaw = parseInt(currentInput.max || '', 10);
    const max = Number.isFinite(maxRaw) ? maxRaw : null;
    const minus = currentLine.querySelector('[data-wub-drawer-quantity-action="minus"]');
    const plus = currentLine.querySelector('[data-wub-drawer-quantity-action="plus"]');
    const focused = document.activeElement;

    if (minus) minus.disabled = displayQuantity <= min;
    if (plus) plus.disabled = max !== null && displayQuantity >= max;

    if ((focused === minus && minus?.disabled) || (focused === plus && plus?.disabled)) {
      currentInput.focus({ preventScroll: true });
    }

    return true;
  }

  function syncLineCommerceState(currentLine, nextLine) {
    const currentPrice = currentLine.querySelector('.wub-cart-drawer__price');
    const nextPrice = nextLine.querySelector('.wub-cart-drawer__price');
    if (!currentPrice || !nextPrice) return false;

    currentPrice.replaceWith(nextPrice.cloneNode(true));
    return true;
  }

  function patchDrawerMutationHtml(html, changedKey, quantity) {
    const nextRoot = parseDrawerRoot(html);
    const currentRoot = drawerSectionRoot();
    const currentDrawer = drawer();
    if (!nextRoot || !currentRoot || !currentDrawer) return false;

    if (!currentDrawer.classList.contains('is-open')) return applyDrawerRoot(nextRoot);

    const nextDrawer = nextRoot.querySelector('#WubCartDrawer');
    const currentBody = drawerBody();
    const nextBody = nextDrawer?.querySelector('[data-wub-cart-drawer-body]');
    if (!nextDrawer || !currentBody || !nextBody) return false;

    const nextLines = new Map();
    nextDrawer.querySelectorAll('[data-wub-cart-line-key]').forEach((line) => {
      nextLines.set(line.dataset.wubCartLineKey, line);
    });

    if (quantity > 0 && !nextLines.has(changedKey)) return false;

    const currentLines = Array.from(currentDrawer.querySelectorAll('[data-wub-cart-line-key]'));
    let mismatch = false;

    currentLines.forEach((currentLine) => {
      if (mismatch) return;
      const key = currentLine.dataset.wubCartLineKey;
      const nextLine = nextLines.get(key);

      if (!nextLine) {
        if (key === changedKey && quantity === 0) {
          currentLine.remove();
          return;
        }
        mismatch = true;
        return;
      }

      if (!syncLineCommerceState(currentLine, nextLine)) {
        mismatch = true;
        return;
      }

      if (key === changedKey && !syncQuantityControl(currentLine, nextLine, key)) mismatch = true;
    });

    if (mismatch) return false;

    const remainingKeys = new Set(
      Array.from(currentDrawer.querySelectorAll('[data-wub-cart-line-key]')).map((line) => line.dataset.wubCartLineKey)
    );
    if (remainingKeys.size !== nextLines.size || Array.from(nextLines.keys()).some((key) => !remainingKeys.has(key))) {
      return false;
    }

    currentRoot.dataset.wubCartItemCount = nextRoot.dataset.wubCartItemCount || '0';
    if (nextRoot.dataset.addedLabel) currentRoot.dataset.addedLabel = nextRoot.dataset.addedLabel;

    const currentFooter = currentDrawer.querySelector('.wub-cart-drawer__footer');
    const nextFooter = nextDrawer.querySelector('.wub-cart-drawer__footer');

    if (nextLines.size === 0) {
      currentBody.innerHTML = nextBody.innerHTML;
      currentFooter?.remove();
      window.requestAnimationFrame(() => panel()?.focus({ preventScroll: true }));
    } else if (nextFooter) {
      if (!currentFooter) {
        currentBody.insertAdjacentElement('afterend', nextFooter.cloneNode(true));
      } else {
        const currentTotal = currentFooter.querySelector('.wub-cart-drawer__total strong');
        const nextTotal = nextFooter.querySelector('.wub-cart-drawer__total strong');
        if (currentTotal && nextTotal) currentTotal.innerHTML = nextTotal.innerHTML;
      }
    }

    updateHeaderCount(cartCountFromDrawer());
    return true;
  }

  async function fetchCartState() {
    const response = await fetch(jsonEndpoint(cartUrl()), {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
      cache: 'no-store'
    });
    if (!response.ok) throw new Error(`Cart state failed (${response.status})`);
    return response.json();
  }

  async function fetchDrawerRoot() {
    const sectionId = drawerSectionId();

    try {
      const sectionUrl = new URL(window.location.href);
      sectionUrl.search = '';
      sectionUrl.hash = '';
      sectionUrl.searchParams.set('section_id', sectionId);

      const response = await fetch(sectionUrl.toString(), {
        headers: { Accept: 'text/html' },
        credentials: 'same-origin',
        cache: 'no-store'
      });

      if (response.ok) {
        const root = parseDrawerRoot(await response.text());
        if (root) return root;
      }
    } catch (error) {
      console.warn('WUB cart drawer section refresh failed, trying cart page fallback:', error);
    }

    const fallback = await fetch(cartUrl(), {
      headers: { Accept: 'text/html' },
      credentials: 'same-origin',
      cache: 'no-store'
    });
    if (!fallback.ok) throw new Error(`Cart drawer fallback failed (${fallback.status})`);

    const root = parseDrawerRoot(await fallback.text());
    if (!root) throw new Error('WUB cart drawer markup was not found.');
    return root;
  }

  function addedLabel() {
    return drawerSectionRoot()?.dataset?.addedLabel || i18n('forms', 'addedToCart', 'Added to your cart');
  }

  function showAddedStatus() {
    const status = drawerSectionRoot()?.querySelector('[data-wub-cart-status]');
    if (!status) return;
    status.textContent = addedLabel();
    status.hidden = false;
  }

  function showToast(message = addedLabel()) {
    window.clearTimeout(toastTimer);
    document.querySelector('[data-wub-cart-toast]')?.remove();

    const toast = document.createElement('div');
    toast.className = 'wub-cart-toast';
    toast.dataset.wubCartToast = '';

    const check = document.createElement('span');
    check.className = 'wub-cart-toast__check';
    check.setAttribute('aria-hidden', 'true');
    check.textContent = '\u2713';

    const status = document.createElement('span');
    status.className = 'wub-cart-toast__message';
    status.setAttribute('role', 'status');
    status.setAttribute('aria-live', 'polite');
    status.setAttribute('aria-atomic', 'true');
    status.textContent = message;

    const link = document.createElement('a');
    link.className = 'wub-cart-toast__link';
    link.href = cartUrl();
    link.textContent = i18n('cart', 'viewCart', 'View cart');

    const close = document.createElement('button');
    close.className = 'wub-cart-toast__close';
    close.type = 'button';
    close.setAttribute('aria-label', i18n('cart', 'closeNotice', 'Close notification'));
    close.textContent = '\u00d7';

    toast.append(check, status, link, close);
    document.body.appendChild(toast);

    const dismiss = () => {
      toast.classList.remove('is-visible');
      window.setTimeout(() => toast.remove(), 180);
    };

    close.addEventListener('click', dismiss, { once: true });
    window.requestAnimationFrame(() => toast.classList.add('is-visible'));
    toastTimer = window.setTimeout(dismiss, 4200);
  }

  function focusableElements() {
    const currentPanel = panel();
    if (!currentPanel) return [];
    return Array.from(currentPanel.querySelectorAll(
      'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])'
    )).filter((element) => !element.hidden && element.getAttribute('aria-hidden') !== 'true');
  }

  function openDrawer({ added = false, trigger = null } = {}) {
    const currentDrawer = drawer();
    const currentPanel = panel();
    if (!currentDrawer || !currentPanel) return false;

    const alreadyOpen = currentDrawer.classList.contains('is-open');
    if (trigger) lastTrigger = trigger;

    currentDrawer.classList.add('is-open');
    currentDrawer.setAttribute('aria-hidden', 'false');
    document.body.classList.add('wub-commerce-locked');
    if (added) showAddedStatus();

    if (!alreadyOpen) {
      window.requestAnimationFrame(() => currentPanel.focus({ preventScroll: true }));
      emit('wub:cart-drawer-opened', {
        source: added ? 'add-to-cart' : 'manual',
        item_count: cartCountFromDrawer()
      });
    }
    return true;
  }

  function closeDrawer({ restoreFocus = true } = {}) {
    const currentDrawer = drawer();
    if (!currentDrawer) return;
    currentDrawer.classList.remove('is-open');
    currentDrawer.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('wub-commerce-locked');
    if (restoreFocus && lastTrigger?.isConnected) lastTrigger.focus({ preventScroll: true });
  }

  async function refreshDrawer({ open = false, added = false, trigger = null } = {}) {
    if (!drawerRefreshPromise) {
      drawerRefreshPromise = (async () => {
        try {
          const next = await fetchDrawerRoot();
          return applyDrawerRoot(next);
        } catch (error) {
          console.error('WUB cart drawer refresh failed:', error);
          return false;
        } finally {
          drawerRefreshPromise = null;
        }
      })();
    }

    const refreshed = await drawerRefreshPromise;
    if (open && refreshed) return openDrawer({ added, trigger });
    return refreshed;
  }

  function setFormLoading(form, loading) {
    const button = form.querySelector('button[type="submit"], input[type="submit"]');
    if (!button) return;
    if (loading) {
      button.dataset.wubWasDisabled = button.disabled ? 'true' : 'false';
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
    } else {
      button.disabled = button.dataset.wubWasDisabled === 'true';
      button.removeAttribute('aria-busy');
      delete button.dataset.wubWasDisabled;
    }
  }

  function showFormError(form, message) {
    const productForm = form.closest('product-form');
    if (productForm && typeof productForm.handleErrorMessage === 'function') {
      productForm.handleErrorMessage(message || false);
      return;
    }

    const root = form.closest('[data-wub-product-atc], .product-add-to-cart-sticky--wub') || form.parentElement;
    const wrapper = root?.querySelector('.product-form__error-message-wrapper');
    const text = wrapper?.querySelector('.product-form__error-message');
    if (wrapper) wrapper.toggleAttribute('hidden', !message);
    if (text && message) text.textContent = message;
  }

  function normalizeDrawerQuantity(input, rawValue) {
    const min = Math.max(1, parseInt(input?.min || '1', 10) || 1);
    const maxRaw = parseInt(input?.max || '', 10);
    const max = Number.isFinite(maxRaw) ? maxRaw : null;
    const step = Math.max(1, parseInt(input?.step || '1', 10) || 1);
    let value = parseInt(rawValue, 10);
    if (!Number.isFinite(value)) value = min;
    value = Math.max(min, value);
    if (max !== null) value = Math.min(max, value);
    value = min + Math.round((value - min) / step) * step;
    if (max !== null) value = Math.min(max, value);
    return value;
  }

  function syncOptimisticQuantityButtons(line, quantity) {
    const input = line?.querySelector('[data-wub-drawer-quantity-input]');
    if (!input) return;
    const min = Math.max(1, parseInt(input.min || '1', 10) || 1);
    const maxRaw = parseInt(input.max || '', 10);
    const max = Number.isFinite(maxRaw) ? maxRaw : null;
    const minus = line.querySelector('[data-wub-drawer-quantity-action="minus"]');
    const plus = line.querySelector('[data-wub-drawer-quantity-action="plus"]');
    if (minus) minus.disabled = quantity <= min;
    if (plus) plus.disabled = max !== null && quantity >= max;
  }

  function lineByKey(key) {
    return drawer()?.querySelector(`[data-wub-cart-line-key="${escapeCss(key)}"]`) || null;
  }

  function showDrawerLineError(key, message) {
    const errorHost = lineByKey(key)?.querySelector('[data-wub-drawer-line-error]');
    if (!errorHost) return;
    errorHost.textContent = message || i18n('cart', 'updateFailed', 'Could not update your cart.');
    errorHost.hidden = false;
  }

  function clearDrawerLineError(key) {
    const errorHost = lineByKey(key)?.querySelector('[data-wub-drawer-line-error]');
    if (!errorHost) return;
    errorHost.textContent = '';
    errorHost.hidden = true;
  }

  async function handleAddToCart(form, submitter) {
    if (!form.checkValidity()) {
      form.reportValidity();
      return;
    }

    const beforeCount = currentHeaderCount();
    setFormLoading(form, true);
    showFormError(form, false);

    try {
      const sectionId = drawerSectionId();
      const formData = submitter ? new FormData(form, submitter) : new FormData(form);
      formData.delete('checkout');
      formData.delete('return_to');
      formData.set('sections', sectionId);
      formData.set('sections_url', window.location.pathname);

      const response = await fetch(jsonEndpoint(cartAddUrl()), {
        method: 'POST',
        body: formData,
        headers: { Accept: 'application/json' },
        credentials: 'same-origin'
      });
      const data = await response.json();

      if (!response.ok || data.status) {
        throw new Error(data.description || data.message || i18n('cart', 'addFailed', 'This product could not be added to your cart.'));
      }

      let count = beforeCount + (Number(data.quantity) || 1);
      try {
        const cart = await fetchCartState();
        count = Number(cart.item_count || 0);
      } catch (error) {
        console.warn('WUB cart count refresh failed:', error);
      }
      updateHeaderCount(count);

      const renderedHtml = data.sections?.[sectionId] || data.sections?.[DEFAULT_DRAWER_SECTION] || null;
      let drawerReady = renderedHtml ? applyDrawerHtml(renderedHtml) : false;
      if (!drawerReady) drawerReady = await refreshDrawer();

      emitCartUpdated(count, {
        action: 'add',
        variant_id: data.id || null,
        quantity: data.quantity || null
      });
      document.dispatchEvent(new CustomEvent('cart:item-added', {
        detail: { source: SOURCE, item: data, cart: { item_count: count } }
      }));
      emit('wub:cart-added', { item: data, item_count: count, form_id: form.id || null });

      const drawerOpened = drawerReady && openDrawer({ added: true, trigger: submitter || form });
      if (!drawerOpened) showToast();
    } catch (error) {
      console.error('WUB add to cart failed:', error);
      showFormError(form, error.message || i18n('forms', 'requestFailed', 'Something went wrong. Please try again.'));
    } finally {
      setFormLoading(form, false);
    }
  }

  async function performDrawerLineChange(key, quantity) {
    if (!key) return;
    const safeQuantity = Math.max(0, parseInt(quantity, 10) || 0);
    const currentLine = lineByKey(key);
    currentLine?.setAttribute('aria-busy', 'true');
    clearDrawerLineError(key);

    drawerMutationErrors.delete(key);
    try {
      const sectionId = drawerSectionId();
      const response = await fetch(jsonEndpoint(cartChangeUrl()), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({
          id: key,
          quantity: safeQuantity,
          sections: sectionId,
          sections_url: window.location.pathname
        })
      });
      const data = await response.json();
      if (!response.ok || data.errors) {
        throw new Error(data.description || data.message || data.errors || i18n('cart', 'updateFailed', 'Could not update your cart.'));
      }

      if (pendingLineQuantities.get(key) === safeQuantity) pendingLineQuantities.delete(key);

      const renderedHtml = data.sections?.[sectionId] || data.sections?.[DEFAULT_DRAWER_SECTION] || null;
      let drawerReady = renderedHtml ? patchDrawerMutationHtml(renderedHtml, key, safeQuantity) : false;
      if (!drawerReady && renderedHtml) drawerReady = applyDrawerHtml(renderedHtml);
      if (!drawerReady) drawerReady = await refreshDrawer();

      const count = Number(data.item_count ?? cartCountFromDrawer());
      updateHeaderCount(count);
      const action = safeQuantity === 0 ? 'remove' : 'change';
      emitCartUpdated(count, { action, line_key: key, quantity: safeQuantity });
      emit(safeQuantity === 0 ? 'wub:cart-item-removed' : 'wub:cart-quantity-changed', {
        line_key: key,
        quantity: safeQuantity,
        item_count: count
      });
    } catch (error) {
      console.error('WUB cart change failed:', error);
      pendingLineQuantities.delete(key);
      await refreshDrawer();
      drawerMutationErrors.set(key, error);
      showDrawerLineError(key, error.message || i18n('cart', 'updateFailed', 'Could not update your cart.'));
    } finally {
      lineByKey(key)?.removeAttribute('aria-busy');
    }
  }

  function enqueueDrawerLineChange(key, quantity) {
    drawerMutationChain = drawerMutationChain
      .catch(() => {})
      .then(() => performDrawerLineChange(key, quantity));
    return drawerMutationChain;
  }

  function scheduleDrawerLineChange(key, quantity) {
    if (!key) return;
    const safeQuantity = Math.max(1, parseInt(quantity, 10) || 1);
    pendingLineQuantities.set(key, safeQuantity);
    window.clearTimeout(quantityTimers.get(key));
    const timer = window.setTimeout(() => {
      quantityTimers.delete(key);
      enqueueDrawerLineChange(key, safeQuantity);
    }, QUANTITY_DEBOUNCE_MS);
    quantityTimers.set(key, timer);
  }

  function removeDrawerLine(key) {
    if (!key) return;
    window.clearTimeout(quantityTimers.get(key));
    quantityTimers.delete(key);
    pendingLineQuantities.delete(key);
    enqueueDrawerLineChange(key, 0);
  }

  function flushScheduledQuantityChanges() {
    Array.from(quantityTimers.keys()).forEach((key) => {
      window.clearTimeout(quantityTimers.get(key));
      quantityTimers.delete(key);
      const quantity = pendingLineQuantities.get(key);
      if (quantity != null) enqueueDrawerLineChange(key, quantity);
    });
    return drawerMutationChain.then(() => {
      if (drawerMutationErrors.size) throw drawerMutationErrors.values().next().value;
    });
  }

  document.addEventListener('submit', (event) => {
    const form = event.target.closest?.('form');
    if (!form || form.dataset.wubNoAjax === 'true') return;
    const action = form.getAttribute('action') || '';
    if (!action.includes('/cart/add')) return;
    event.preventDefault();
    handleAddToCart(form, event.submitter || null);
  }, true);

  document.addEventListener('click', (event) => {
    const close = event.target.closest?.('[data-wub-cart-close]');
    if (close) {
      event.preventDefault();
      closeDrawer();
      return;
    }

    const upsellAdd = event.target.closest?.('[data-upsell-atc], [data-wub-upsell-add]');
    if (upsellAdd) {
      pendingExternalAdd = true;
      pendingExternalTrigger = upsellAdd;
    }

    const line = event.target.closest?.('[data-wub-cart-line-key]');
    if (!line) return;
    const key = line.dataset.wubCartLineKey;

    const remove = event.target.closest?.('[data-wub-drawer-remove]');
    if (remove) {
      event.preventDefault();
      removeDrawerLine(key);
      return;
    }

    const actionButton = event.target.closest?.('[data-wub-drawer-quantity-action]');
    if (actionButton) {
      event.preventDefault();
      const input = line.querySelector('[data-wub-drawer-quantity-input]');
      if (!input) return;
      const current = normalizeDrawerQuantity(input, input.value);
      const step = Math.max(1, parseInt(input.step || '1', 10) || 1);
      const min = Math.max(1, parseInt(input.min || '1', 10) || 1);
      const rawNext = actionButton.dataset.wubDrawerQuantityAction === 'plus'
        ? current + step
        : Math.max(min, current - step);
      const next = normalizeDrawerQuantity(input, rawNext);
      input.value = String(next);
      syncOptimisticQuantityButtons(line, next);
      if (next !== current) scheduleDrawerLineChange(key, next);
    }
  });

  document.addEventListener('change', (event) => {
    const input = event.target.closest?.('[data-wub-drawer-quantity-input]');
    if (!input) return;
    const line = input.closest('[data-wub-cart-line-key]');
    if (!line) return;
    const next = normalizeDrawerQuantity(input, input.value);
    input.value = String(next);
    syncOptimisticQuantityButtons(line, next);
    scheduleDrawerLineChange(line.dataset.wubCartLineKey, next);
  });

  document.addEventListener('keydown', (event) => {
    const currentDrawer = drawer();
    if (!currentDrawer?.classList.contains('is-open')) return;

    if (event.key === 'Escape') {
      event.preventDefault();
      closeDrawer();
      return;
    }

    if (event.key !== 'Tab') return;
    const focusable = focusableElements();
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  });

  document.addEventListener('click', (event) => {
    const checkout = event.target.closest?.('[data-wub-checkout]');
    if (!checkout) return;
    const form = checkout.closest('form');
    if (!form) return;

    if (checkout.dataset.wubCheckoutSubmitting === 'true') {
      event.preventDefault();
      return;
    }

    event.preventDefault();
    checkout.dataset.wubCheckoutSubmitting = 'true';
    form.setAttribute('aria-busy', 'true');

    flushScheduledQuantityChanges()
      .then(() => {
        checkout.dataset.wubCheckoutTracked = 'true';
        emit('wub:checkout-click', {
          source: checkout.dataset.wubCheckoutSource || 'unknown',
          item_count: cartCountFromDrawer()
        });
        form.requestSubmit(checkout);
      })
      .catch(() => {
        form.removeAttribute('aria-busy');
        delete checkout.dataset.wubCheckoutSubmitting;
      });
  }, true);

  document.addEventListener('cart:updated', (event) => {
    if (event.detail?.source === SOURCE || event.detail?.source === 'wub-cart') return;
    window.clearTimeout(externalRefreshTimer);
    externalRefreshTimer = window.setTimeout(async () => {
      const shouldOpen = pendingExternalAdd;
      const trigger = pendingExternalTrigger;
      pendingExternalAdd = false;
      pendingExternalTrigger = null;

      const drawerOpened = await refreshDrawer({ open: shouldOpen, added: shouldOpen, trigger });
      if (shouldOpen && !drawerOpened) showToast();
    }, 120);
  });

  window.WUBCommerce = Object.freeze({
    openDrawer,
    closeDrawer,
    refreshDrawer,
    applyDrawerHtml,
    changeDrawerLine: enqueueDrawerLineChange,
    showToast
  });

  updateHeaderCount(cartCountFromDrawer());
})();
